import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import withProjectPermissions from '../components/withProjectPermissions';
import VoicePlayer from '../components/VoicePlayer';
import ActivityMediaModal from '../components/ActivityMediaModal';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage';
import { db, auth, storage } from '../firebase/firebase';
import { serverTimestamp } from 'firebase/firestore';
import VoiceRecorder from '../components/VoiceRecorder';
import DateRangePicker from '../components/DateRangePicker';
import ActivityUploadModal from '../components/ActivityUploadModal';
import { Plus, AlertTriangle, ArrowLeft, Calendar, FileDown, Settings } from 'lucide-react';
import './ActivityView.css';
import { buildActivityExportPdf, buildActivitySectionsReportPdf } from '../utils/activityExportPdf';
import ReportSectionModal from '../components/ReportSectionModal';
import ReportSectionsPanel from '../components/ReportSectionsPanel';
import { useBranding } from '../contexts/BrandingContext';
import { getCompanyLogoUrl } from '../utils/branding';
import PdfExportSettingsModal from '../components/PdfExportSettingsModal';
import { loadActivityPdfSettings, saveActivityPdfSettings } from '../utils/pdfExportSettings';
import { activityMediaFilenameExtension, downloadRemoteFile } from '../utils/activityMediaUtils';

/** Local calendar date as YYYY-MM-DD (for filter comparison with upload timestamps). */
function toLocalYmd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Monday–Sunday of the week containing `d` (local). */
function getThisWeekYmdRange(d = new Date()) {
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + mondayOffset);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return { start: toLocalYmd(monday), end: toLocalYmd(sunday) };
}

function getThisMonthYmdRange(d = new Date()) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { start: toLocalYmd(first), end: toLocalYmd(last) };
}

function uploadMatchesDateFilter(upload, filterStartDate, filterEndDate) {
  if (!upload.timestamp) return false;
  const t = upload.timestamp;
  const dateObj = t?.toDate ? t.toDate() : new Date(t);
  if (Number.isNaN(dateObj.getTime())) return false;
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd}`;
  if (filterStartDate && filterEndDate) {
    return dateStr >= filterStartDate && dateStr <= filterEndDate;
  }
  if (filterStartDate) {
    return dateStr >= filterStartDate;
  }
  if (filterEndDate) {
    return dateStr <= filterEndDate;
  }
  return true;
}


function ActivityLanding({ permissions }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [workUploads, setWorkUploads] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({});
  /** True while project member map is loading; avoids flashing "Deleted User" before `users` is populated. */
  const [usersMapLoading, setUsersMapLoading] = useState(true);
  const [mediaModal, setMediaModal] = useState({ open: false, items: [], initialIndex: 0 });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [compactView, setCompactView] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  /** 'all' | 'today' | 'week' | 'month' | 'custom' */
  const [datePreset, setDatePreset] = useState('all');
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [modalRangeStart, setModalRangeStart] = useState('');
  const [modalRangeEnd, setModalRangeEnd] = useState('');
  const [voiceNotes, setVoiceNotes] = useState({});
  const [teams, setTeams] = useState([]);
  const [loadedImages, setLoadedImages] = useState(new Set()); // Track loaded images
  const [showUploadModal, setShowUploadModal] = useState(false);
  /** When true, show selection UI for PDF export (checkboxes, etc.) */
  const [exportSelectionMode, setExportSelectionMode] = useState(false);
  const [exportSelectedIds, setExportSelectedIds] = useState(() => new Set());
  const selectAllVisibleInputRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [activityPdfSettings, setActivityPdfSettings] = useState(() => loadActivityPdfSettings());
  const [pdfExportSettingsOpen, setPdfExportSettingsOpen] = useState(false);
  const { company: brandingCompany } = useBranding();
  const companyLogoUrl = getCompanyLogoUrl(brandingCompany);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploadToDelete, setUploadToDelete] = useState(null);
  const [commentsByUpload, setCommentsByUpload] = useState({});
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [commentModal, setCommentModal] = useState({ open: false, uploadId: null, parentCommentId: null });
  const [deleteCommentModal, setDeleteCommentModal] = useState({ open: false, uploadId: null, commentId: null, comment: null });
  const [reportSections, setReportSections] = useState([]);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [isSectionExporting, setIsSectionExporting] = useState(false);

  const currentUserId = auth?.currentUser?.uid;
  const currentUserName = auth?.currentUser?.displayName || auth?.currentUser?.email || 'Unknown User';

  const getWorkUploadAuthorLabel = useCallback(
    (upload) => {
      if (!upload?.userId) return upload?.createdByName || 'Deleted User';
      const profile = users[upload.userId];
      if (profile) {
        const name = profile.name || [profile.firstName, profile.lastName].filter(Boolean).join(' ');
        return name || 'Unknown';
      }
      if (usersMapLoading) return 'Loading…';
      return upload?.createdByName || 'Deleted User';
    },
    [users, usersMapLoading]
  );

  const isPM = Boolean(project && (
    project.createdBy === currentUserId ||
    (project.collaborators?.[currentUserId]?.status === 'accepted' && project.collaborators?.[currentUserId]?.role === 'pm_collaborator')
  ));

  // Check if current user can delete any comment (PM)
  const canDeleteAnyComment = isPM;

  const isReadOnly = project?.status === 'closed' || project?.isFinished || !permissions?.canEdit;

  const handleDeleteUpload = async (upload) => {
    if (isReadOnly) return;
    setDeletingId(upload.id);
    try {
      // Delete Firestore doc first so post disappears immediately
      await deleteDoc(doc(db, `projects/${projectId}/work_uploads/${upload.id}`));

      // Best-effort: delete Storage files (images + voice note)
      const userId = upload.userId;
      const uploadId = upload.id;

      // Delete images: projects/{projectId}/work_uploads/{userId}/{uploadId}_0.jpg, _1.jpg, ...
      const imageUrls = upload.imageUrls || (upload.imageUrl ? [upload.imageUrl] : []);
      for (let i = 0; i < imageUrls.length; i++) {
        try {
          const imageRef = ref(storage, `projects/${projectId}/work_uploads/${userId}/${uploadId}_${i}.jpg`);
          await deleteObject(imageRef);
        } catch (e) {
          console.warn(`Could not delete image ${i}:`, e.message);
        }
      }

      // Delete voice note - try webm and mp4 (one will succeed)
      if (upload.voiceNoteUrl) {
        for (const ext of ['webm', 'mp4']) {
          try {
            const voiceRef = ref(storage, `projects/${projectId}/work_uploads/${userId}/voice_notes/${uploadId}.${ext}`);
            await deleteObject(voiceRef);
            break;
          } catch {
            // File doesn't exist with this extension, try next
          }
        }
      }
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDeleteUpload = () => {
    if (uploadToDelete) {
      handleDeleteUpload(uploadToDelete);
      setShowDeleteConfirm(false);
      setUploadToDelete(null);
    }
  };

  const toggleComments = (uploadId) => {
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(uploadId)) next.delete(uploadId);
      else next.add(uploadId);
      return next;
    });
  };

  const handleAddComment = async (uploadId, commentText, attachedFiles, voiceNoteFile, parentCommentId = null) => {
    if (isReadOnly) return;
    if (!commentText?.trim() && (!attachedFiles?.length) && !voiceNoteFile) return;
    try {
      let photoUrls = [];
      let voiceNoteUrl = null;
      const timestamp = Date.now();

      if (attachedFiles?.length > 0) {
        for (let i = 0; i < attachedFiles.length; i++) {
          const file = attachedFiles[i];
          const storageRef = ref(storage, `projects/${projectId}/activity_comments/${uploadId}/media/${timestamp}_${i}.jpg`);
          await uploadBytes(storageRef, file);
          photoUrls.push(await getDownloadURL(storageRef));
        }
      }
      if (voiceNoteFile) {
        const ext = voiceNoteFile.name.split('.').pop() || 'mp4';
        const voiceRef = ref(storage, `projects/${projectId}/activity_comments/${uploadId}/media/${timestamp}_voice.${ext}`);
        await uploadBytes(voiceRef, voiceNoteFile);
        voiceNoteUrl = await getDownloadURL(voiceRef);
      }

      const commentData = {
        comment: commentText?.trim() || '',
        createdBy: currentUserId,
        createdByName: currentUserName,
        createdAt: serverTimestamp(),
        ...(photoUrls.length > 0 && { photoUrls }),
        ...(voiceNoteUrl && { voiceNoteUrl }),
        ...(parentCommentId && { parentCommentId }),
      };

      const docRef = await addDoc(
        collection(db, `projects/${projectId}/work_uploads/${uploadId}/comments`),
        commentData
      );

      const newComment = {
        id: docRef.id,
        ...commentData,
        createdAt: new Date()
      };

      setCommentsByUpload(prev => {
        const next = { ...prev };
        const list = next[uploadId] || [];
        next[uploadId] = [...list, newComment];
        return next;
      });
      setCommentModal({ open: false, uploadId: null, parentCommentId: null });
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment. Please try again.');
    }
  };

  const handleDeleteComment = async (uploadId, commentId) => {
    try {
      await deleteDoc(doc(db, `projects/${projectId}/work_uploads/${uploadId}/comments/${commentId}`));
      setCommentsByUpload(prev => {
        const next = { ...prev };
        const list = (next[uploadId] || []).filter(c => c.id !== commentId);
        next[uploadId] = list;
        return next;
      });
      setDeleteCommentModal({ open: false, uploadId: null, commentId: null, comment: null });
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const confirmDeleteComment = () => {
    if (deleteCommentModal.uploadId && deleteCommentModal.commentId) {
      handleDeleteComment(deleteCommentModal.uploadId, deleteCommentModal.commentId);
    }
  };

  const canDeleteComment = (comment) => {
    return comment.createdBy === currentUserId || canDeleteAnyComment;
  };

  const ActivityCommentModal = ({ open, onClose, uploadId, parentCommentId, onSubmit }) => {
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [voiceNoteFile, setVoiceNoteFile] = useState(null);

    const handleFileSelect = (e) => {
      const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
      setAttachedFiles(prev => [...prev, ...files]);
    };

    const handleClose = () => {
      if (!submitting) {
        setComment('');
        setAttachedFiles([]);
        setVoiceNoteFile(null);
        onClose();
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if ((!comment.trim() && !attachedFiles.length && !voiceNoteFile) || !uploadId) return;
      setSubmitting(true);
      try {
        await onSubmit(uploadId, comment, attachedFiles, voiceNoteFile, parentCommentId);
      } finally {
        setSubmitting(false);
      }
    };

    if (!open) return null;

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }}>
        <div style={{
          background: 'var(--cc-card-bg)', padding: 24, borderRadius: 12, width: '90%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', boxSizing: 'border-box'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
              {parentCommentId ? 'Reply' : 'Add comment'}
            </h3>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--cc-text-muted)' }}>×</button>
          </div>
          <form onSubmit={handleSubmit}>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder=""
              style={{
                width: '100%',
                height: 80,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontSize: 14,
                fontFamily: 'inherit',
                marginBottom: 12,
                resize: 'none',
                boxSizing: 'border-box',
                backgroundColor: submitting ? 'var(--branding-app-surface-panel)' : 'var(--cc-card-bg)'
              }}
              disabled={submitting}
            />
            <div style={{ marginBottom: 12 }}>
              <input type="file" accept="image/*" multiple onChange={handleFileSelect} disabled={submitting} style={{ display: 'none' }} id="activity-comment-files" />
              <label htmlFor="activity-comment-files" style={{
                display: 'inline-block', background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', padding: '8px 16px', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14
              }}>Add photos</label>
              {attachedFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {attachedFiles.map((file, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={URL.createObjectURL(file)} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                      <button type="button" onClick={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))} style={{
                        position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14
                      }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: 6, fontSize: 14 }}>Voice note (optional)</label>
              <VoiceRecorder onVoiceRecorded={f => setVoiceNoteFile(f)} disabled={submitting} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" onClick={handleClose} disabled={submitting} style={{ background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button type="submit" disabled={submitting || (!comment.trim() && !attachedFiles.length && !voiceNoteFile)} style={{
                background: (submitting || (!comment.trim() && !attachedFiles.length && !voiceNoteFile)) ? '#cbd5e1' : 'var(--branding-app-primary)',
                color: (submitting || (!comment.trim() && !attachedFiles.length && !voiceNoteFile)) ? '#64748b' : 'var(--branding-app-primary-fg)', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: (submitting || (!comment.trim() && !attachedFiles.length && !voiceNoteFile)) ? 'not-allowed' : 'pointer', fontSize: 14
              }}>{submitting ? 'Posting...' : 'Post'}</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [projectId]);

  // Fetch users for mapping userIds to names
  // Smart approach: Only fetch users who are part of THIS project
  // Handles both Subs (in memberDetails) and PM collaborators
  useEffect(() => {
    const fetchProjectUsers = async () => {
      if (!projectId) {
        setUsersMapLoading(false);
        return;
      }

      setUsersMapLoading(true);
      try {
        const usersMap = {};
        
        // 1️⃣ Get project owner (PM)
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const ownerId = projectData.createdBy;
          
          if (ownerId) {
            const ownerDoc = await getDoc(doc(db, 'users', ownerId));
            if (ownerDoc.exists()) {
              usersMap[ownerId] = ownerDoc.data();
              console.log('✅ Loaded project owner:', ownerId);
            }
          }
          
          // 2️⃣ Get PM collaborators from project.collaborators
          const collaborators = projectData.collaborators || {};
          for (const [userId, collaboratorData] of Object.entries(collaborators)) {
            if (collaboratorData.status === 'accepted') {
              try {
                const collaboratorDoc = await getDoc(doc(db, 'users', userId));
                if (collaboratorDoc.exists()) {
                  usersMap[userId] = collaboratorDoc.data();
                  console.log('✅ Loaded PM collaborator:', userId);
                }
              } catch (error) {
                console.warn(`⚠️ Could not fetch collaborator ${userId}:`, error);
              }
            }
          }
        }
        
        // 3️⃣ Get Subs from memberDetails across all teams
        const teamsSnapshot = await getDocs(collection(db, `projects/${projectId}/teams`));
        
        for (const teamDoc of teamsSnapshot.docs) {
          const teamId = teamDoc.id;
          
          try {
            const membersSnapshot = await getDocs(
              collection(db, `projects/${projectId}/teams/${teamId}/memberDetails`)
            );
            
            membersSnapshot.forEach(memberDoc => {
              const memberData = memberDoc.data();
              // Store member data directly from memberDetails (has firstName, lastName, etc.)
              usersMap[memberDoc.id] = memberData;
            });
            
            console.log(`✅ Loaded ${membersSnapshot.docs.length} members from team ${teamId}`);
          } catch (error) {
            console.warn(`⚠️ Could not fetch members for team ${teamId}:`, error);
          }
        }
        
        console.log(`📊 Total users loaded for activity feed: ${Object.keys(usersMap).length}`);
        setUsers(usersMap);
        
      } catch (error) {
        console.error('Error fetching project users:', error);
      } finally {
        setUsersMapLoading(false);
      }
    };
    
    fetchProjectUsers();
  }, [projectId]);

  // Fetch all teams (same as TeamsLanding)
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const teamSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = teamSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsData);
        console.log('Teams loaded:', teamsData);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    })();
  }, [projectId]);

  // Debug: Log team IDs from uploads
  useEffect(() => {
    if (workUploads.length > 0) {
      console.log('Uploads with team IDs:', workUploads.map(upload => ({
        uploadId: upload.id,
        teamId: upload.teamId,
        teamName: upload.teamName
      })));
    }
  }, [workUploads]);

  // Fetch voice notes for uploads (prefer Firestore voiceNoteUrl; fallback to Storage path for legacy)
  useEffect(() => {
    const fetchVoiceNotes = async () => {
      if (!projectId) return;

      const voiceNotesMap = {};

      for (const upload of workUploads) {
        // Prefer voiceNoteUrl from Firestore (supports webm from web, mp4 from iOS)
        if (upload.voiceNoteUrl) {
          voiceNotesMap[upload.id] = upload.voiceNoteUrl;
          continue;
        }
        if (upload.userId) {
          try {
            const voiceRef = ref(storage, `projects/${projectId}/work_uploads/${upload.userId}/voice_notes/${upload.id}.mp4`);
            const voiceUrl = await getDownloadURL(voiceRef);
            voiceNotesMap[upload.id] = voiceUrl;
          } catch {
            // Voice note doesn't exist for this upload
          }
        }
      }

      setVoiceNotes(voiceNotesMap);
    };

    if (workUploads.length > 0) {
      fetchVoiceNotes();
    }
  }, [workUploads, projectId]);

  // Subscribe to real-time work uploads
  useEffect(() => {
    if (!projectId) return;
    const workUploadsRef = collection(db, `projects/${projectId}/work_uploads`);
    const workUploadsQuery = query(
      workUploadsRef,
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    const unsubscribe = onSnapshot(workUploadsQuery, (snapshot) => {
      const uploadsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }));
      setWorkUploads(uploadsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching work uploads:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [projectId]);

  // Fetch comments for each upload
  useEffect(() => {
    if (!projectId || workUploads.length === 0) return;
    const fetchComments = async () => {
      const commentsObj = {};
      for (const upload of workUploads) {
        try {
          const commentsQuery = query(
            collection(db, `projects/${projectId}/work_uploads/${upload.id}/comments`),
            orderBy('createdAt', 'asc')
          );
          const commentsSnap = await getDocs(commentsQuery);
          const commentsData = commentsSnap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || null
          }));
          commentsObj[upload.id] = commentsData;
        } catch (err) {
          console.warn('Error fetching comments for upload', upload.id, err);
          commentsObj[upload.id] = [];
        }
      }
      setCommentsByUpload(prev => ({ ...prev, ...commentsObj }));
    };
    fetchComments();
  }, [projectId, workUploads]);

  // Load PM report sections (PM-only, real-time)
  useEffect(() => {
    if (!projectId || !isPM) return;
    const q = query(
      collection(db, `projects/${projectId}/report_sections`),
      orderBy('order', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setReportSections(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.sectionType === 'activity')
      );
    });
    return () => unsub();
  }, [projectId, isPM]);

  const handleSaveSection = async ({ name, annotation }) => {
    if (editingSection) {
      await updateDoc(doc(db, `projects/${projectId}/report_sections/${editingSection.id}`), { name, annotation });
    } else {
      await addDoc(collection(db, `projects/${projectId}/report_sections`), {
        sectionType: 'activity',
        name,
        annotation,
        itemIds: Array.from(exportSelectedIds),
        createdAt: serverTimestamp(),
        createdBy: currentUserId,
        order: Date.now(),
      });
      setExportSelectedIds(new Set());
    }
    setSectionModalOpen(false);
    setEditingSection(null);
  };

  const handleDeleteSection = async (section) => {
    await deleteDoc(doc(db, `projects/${projectId}/report_sections/${section.id}`));
  };

  const handleExportSectionReport = async (checkedSections) => {
    if (!checkedSections.length) return;
    setIsSectionExporting(true);
    try {
      const sections = checkedSections.map(section => {
        const posts = (section.itemIds || [])
          .map(id => workUploads.find(u => u.id === id))
          .filter(Boolean)
          .sort((a, b) => {
            const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
            const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
            return ta - tb;
          })
          .map(upload => {
            const label = getWorkUploadAuthorLabel(upload);
            const vidCount = getVideoUrls(upload).length;
            return {
              teamName: upload.teamName || '—',
              authorName: label === 'Deleted User' ? 'Deleted user' : label === 'Loading…' ? '' : label,
              timeLabel: formatTimestamp(upload.timestamp),
              body: upload.comment || '',
              imageUrls: getImageUrls(upload.imageUrl, upload.imageUrls, upload.id),
              voiceLine: voiceNotes[upload.id] ? 'Voice note: not included in this PDF — use the app to listen.' : null,
              videoLine: vidCount > 0 ? `${vidCount} video clip${vidCount > 1 ? 's' : ''} not embedded in this PDF — use Download or the app.` : null,
              thread: buildCommentThreadForExport(upload.id),
            };
          });
        return { name: section.name, annotation: section.annotation || '', posts };
      });
      const { includeCompanyLogo: useLogo, ...activityContentOptions } = activityPdfSettings;
      await buildActivitySectionsReportPdf({
        projectName: project?.name || 'Project',
        sections,
        contentOptions: activityContentOptions,
        companyLogoUrl: useLogo && companyLogoUrl ? companyLogoUrl : null,
      });
    } catch (err) {
      console.error('Section report export failed:', err);
      alert('Could not generate PDF. Check your connection and try again.');
    } finally {
      setIsSectionExporting(false);
    }
  };

  const formatTimestamp = React.useCallback((timestamp) => {
    if (!timestamp) return '';

    let dateObj;
    if (timestamp instanceof Date) {
      dateObj = timestamp;
    } else if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
      dateObj = timestamp.toDate();
    } else {
      dateObj = new Date(timestamp);
    }

    if (Number.isNaN(dateObj.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  }, []);

  // Get team color from Firestore
  const getTeamColor = (teamId) => {
    if (!teamId) return '#e2e8f0';
    const team = teams.find(t => t.id === teamId);
    return team?.color || '#e2e8f0';
  };

  // Gather unique teams from workUploads
  const uniqueTeams = React.useMemo(() => {
    const teamMap = {};
    workUploads.forEach(upload => {
      if (upload.teamId && upload.teamName) {
        teamMap[upload.teamId] = upload.teamName;
      }
    });
    return Object.entries(teamMap).map(([id, name]) => ({ id, name }));
  }, [workUploads]);

  // Filter uploads by selected team
  const filteredUploads = selectedTeam
    ? workUploads.filter(u => u.teamId === selectedTeam)
    : workUploads;

  const uploadsMatchingDate = React.useMemo(
    () => filteredUploads.filter((u) => uploadMatchesDateFilter(u, filterStartDate, filterEndDate)),
    [filteredUploads, filterStartDate, filterEndDate]
  );

  const visibleExportIds = React.useMemo(
    () => uploadsMatchingDate.map((u) => u.id),
    [uploadsMatchingDate],
  );
  const allVisibleSelected =
    visibleExportIds.length > 0 && visibleExportIds.every((id) => exportSelectedIds.has(id));
  const someVisibleSelected =
    visibleExportIds.length > 0 && visibleExportIds.some((id) => exportSelectedIds.has(id));

  useEffect(() => {
    const el = selectAllVisibleInputRef.current;
    if (el) {
      el.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected, exportSelectionMode]);

  const applyDatePreset = (preset) => {
    if (preset === 'all') {
      setFilterStartDate('');
      setFilterEndDate('');
      setDatePreset('all');
      return;
    }
    if (preset === 'today') {
      const y = toLocalYmd(new Date());
      setFilterStartDate(y);
      setFilterEndDate(y);
      setDatePreset('today');
      return;
    }
    if (preset === 'week') {
      const { start, end } = getThisWeekYmdRange();
      setFilterStartDate(start);
      setFilterEndDate(end);
      setDatePreset('week');
      return;
    }
    if (preset === 'month') {
      const { start, end } = getThisMonthYmdRange();
      setFilterStartDate(start);
      setFilterEndDate(end);
      setDatePreset('month');
    }
  };

  const openDateRangeModal = () => {
    setModalRangeStart(filterStartDate);
    setModalRangeEnd(filterEndDate);
    setShowDateRangeModal(true);
  };

  const applyCustomDateRange = () => {
    setFilterStartDate(modalRangeStart);
    setFilterEndDate(modalRangeEnd);
    if (!modalRangeStart && !modalRangeEnd) {
      setDatePreset('all');
    } else {
      setDatePreset('custom');
    }
    setShowDateRangeModal(false);
  };

  // Memoize image URLs per upload to avoid re-processing on every render
  // Handles both new format (imageUrls array) and old format (imageUrl string)
  const parsedImageUrls = React.useMemo(() => {
    const cache = {};
    workUploads.forEach(upload => {
      if (upload.id) {
        if (!cache[upload.id]) {
          // New format: imageUrls array (preferred)
          if (upload.imageUrls && Array.isArray(upload.imageUrls) && upload.imageUrls.length > 0) {
            cache[upload.id] = upload.imageUrls
              .map(url => url.trim())
              .filter(url => url.length > 0);
          }
          // Old format: imageUrl comma-separated string (backward compatibility)
          else if (upload.imageUrl && typeof upload.imageUrl === 'string' && upload.imageUrl.length > 0) {
            cache[upload.id] = upload.imageUrl
              .split(',')
              .map(url => url.trim())
              .filter(url => url.length > 0);
          } else {
            cache[upload.id] = [];
          }
        }
      }
    });
    return cache;
  }, [workUploads]);

  // Helper function to get cached parsed URLs (or parse on the fly for edge cases)
  const getImageUrls = React.useCallback((imageUrl, imageUrls, uploadId) => {
    // Use cached version if available
    if (uploadId && parsedImageUrls[uploadId]) {
      return parsedImageUrls[uploadId];
    }
    // Fallback: try new format first (array), then old format (string)
    if (imageUrls && Array.isArray(imageUrls) && imageUrls.length > 0) {
      return imageUrls.map(url => url.trim()).filter(url => url.length > 0);
    }
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.length > 0) {
      return imageUrl.split(',').map(url => url.trim()).filter(url => url.length > 0);
    }
    return [];
  }, [parsedImageUrls]);

  const getVideoUrls = React.useCallback((upload) => {
    if (!upload?.videoUrls || !Array.isArray(upload.videoUrls)) return [];
    return upload.videoUrls.map((u) => String(u).trim()).filter((u) => u.length > 0);
  }, []);

  const getWorkUploadMediaItems = React.useCallback(
    (upload) => {
      const imgs = getImageUrls(upload.imageUrl, upload.imageUrls, upload.id);
      const vids = getVideoUrls(upload);
      return [
        ...imgs.map((url) => ({ type: 'image', url })),
        ...vids.map((url) => ({ type: 'video', url })),
      ];
    },
    [getImageUrls, getVideoUrls],
  );

  /** At least one selected post has a photo (PDF embeds images only, not video). */
  const selectionIncludesPdfPhotos = React.useMemo(() => {
    if (exportSelectedIds.size === 0) return false;
    for (const u of uploadsMatchingDate) {
      if (
        exportSelectedIds.has(u.id) &&
        getImageUrls(u.imageUrl, u.imageUrls, u.id).length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [uploadsMatchingDate, exportSelectedIds, getImageUrls]);

  useEffect(() => {
    if (!exportSelectionMode) {
      setExportSelectedIds(new Set());
    }
  }, [exportSelectionMode]);

  useEffect(() => {
    saveActivityPdfSettings(activityPdfSettings);
  }, [activityPdfSettings]);

  const getExportFilterSummary = React.useCallback(() => {
    const teamLabel = selectedTeam
      ? (uniqueTeams.find(t => t.id === selectedTeam)?.name || 'Team')
      : 'All teams';
    let dateLabel = 'All dates';
    if (datePreset === 'today') dateLabel = 'Today';
    else if (datePreset === 'week') dateLabel = 'This week (Mon–Sun)';
    else if (datePreset === 'month') dateLabel = 'This month';
    else if (datePreset === 'custom') {
      if (filterStartDate && filterEndDate) dateLabel = `${filterStartDate} – ${filterEndDate}`;
      else if (filterStartDate) dateLabel = `From ${filterStartDate}`;
      else if (filterEndDate) dateLabel = `Until ${filterEndDate}`;
      else dateLabel = 'All dates';
    } else if (filterStartDate && filterEndDate) {
      dateLabel = `${filterStartDate} – ${filterEndDate}`;
    } else if (filterStartDate) {
      dateLabel = `From ${filterStartDate}`;
    } else if (filterEndDate) {
      dateLabel = `Until ${filterEndDate}`;
    } else {
      dateLabel = 'All dates';
    }
    return `${teamLabel} · ${dateLabel}`;
  }, [selectedTeam, uniqueTeams, datePreset, filterStartDate, filterEndDate]);

  const getCommentTime = (c) => {
    const d = c?.createdAt;
    if (!d) return 0;
    if (d instanceof Date) return d.getTime();
    if (typeof d.toDate === 'function') return d.toDate().getTime();
    const t = new Date(d).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const buildCommentThreadForExport = React.useCallback(
    (uploadId) => {
      const list = commentsByUpload[uploadId] || [];
      if (!list.length) return [];
      const byParent = new Map();
      for (const c of list) {
        const key = c.parentCommentId || '__root__';
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(c);
      }
      for (const arr of byParent.values()) {
        arr.sort((a, b) => getCommentTime(a) - getCommentTime(b));
      }
      const thread = [];
      const visit = (c, depth) => {
        const timeLabel = c.createdAt ? formatTimestamp(c.createdAt) : '';
        thread.push({
          depth,
          name: c.createdByName || 'Unknown',
          timeLabel,
          text: c.comment || '',
          imageUrls: Array.isArray(c.photoUrls) ? c.photoUrls.filter(Boolean) : [],
          voiceLine: c.voiceNoteUrl
            ? 'Voice note: not included in this PDF — use the app to listen.'
            : null,
        });
        const children = byParent.get(c.id) || [];
        for (const ch of children) visit(ch, depth + 1);
      };
      const roots = byParent.get('__root__') || [];
      for (const r of roots) visit(r, 0);
      return thread;
    },
    [commentsByUpload, formatTimestamp]
  );

  const toggleExportSelect = (id) => {
    setExportSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisibleForExport = () => {
    setExportSelectedIds((prev) => {
      const next = new Set(prev);
      if (visibleExportIds.length === 0) return next;
      if (allVisibleSelected) {
        visibleExportIds.forEach((id) => next.delete(id));
      } else {
        visibleExportIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearExportSelection = () => {
    setExportSelectedIds(new Set());
  };

  const handleGenerateActivityPdf = async () => {
    if (reportSections.length > 0) {
      handleExportSectionReport(reportSections);
      return;
    }
    if (exportSelectedIds.size === 0) return;
    if (usersMapLoading) {
      alert('Still loading member names. Please wait a moment and try again.');
      return;
    }
    setIsExporting(true);
    try {
      const idSet = exportSelectedIds;
      const selected = uploadsMatchingDate
        .filter((u) => idSet.has(u.id))
        .sort((a, b) => {
          const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
          const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
          return ta - tb;
        });
      const posts = selected.map((upload) => {
        const label = getWorkUploadAuthorLabel(upload);
        const authorName =
          label === 'Deleted User' ? 'Deleted user' : label === 'Loading…' ? 'Loading…' : label;
        const vidCount = getVideoUrls(upload).length;
        return {
          teamName: upload.teamName || '—',
          authorName,
          timeLabel: formatTimestamp(upload.timestamp),
          body: upload.comment || '',
          imageUrls: getImageUrls(upload.imageUrl, upload.imageUrls, upload.id),
          voiceLine: voiceNotes[upload.id]
            ? 'Voice note: not included in this PDF — use the app to listen.'
            : null,
          videoLine:
            vidCount > 0
              ? `${vidCount} video clip${vidCount > 1 ? 's' : ''} not embedded in this PDF — use Download or the app.`
              : null,
          thread: buildCommentThreadForExport(upload.id),
        };
      });
      const { includeCompanyLogo: useLogo, ...activityContentOptions } = activityPdfSettings;
      await buildActivityExportPdf({
        projectName: project?.name || 'Project',
        filterSummary: getExportFilterSummary(),
        posts,
        contentOptions: activityContentOptions,
        companyLogoUrl: useLogo && companyLogoUrl ? companyLogoUrl : null,
      });
    } catch (err) {
      console.error('Activity PDF export failed:', err);
      alert('Could not generate PDF. If images fail to load, check your connection and try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadSelectedMedia = async () => {
    if (exportSelectedIds.size === 0) return;
    setIsDownloading(true);
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      const idSet = exportSelectedIds;
      const selected = uploadsMatchingDate
        .filter((u) => idSet.has(u.id))
        .sort((a, b) => {
          const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : 0;
          const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : 0;
          return ta - tb;
        });

      for (const upload of selected) {
        const images = getImageUrls(upload.imageUrl, upload.imageUrls, upload.id);
        const videos = getVideoUrls(upload);
        let imgIdx = 0;
        for (const url of images) {
          const ext = activityMediaFilenameExtension(url, false);
          await downloadRemoteFile(url, `activity_${upload.id}_${imgIdx++}.${ext}`);
          await wait(350);
        }
        let vidIdx = 0;
        for (const url of videos) {
          const ext = activityMediaFilenameExtension(url, true);
          await downloadRemoteFile(url, `activity_${upload.id}_video_${vidIdx++}.${ext}`);
          await wait(350);
        }
      }
    } catch (err) {
      console.error('Bulk download failed:', err);
      alert('Some files could not be downloaded. Check your connection or try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper function to get first image for thumbnail
  // const getFirstImage = React.useCallback((imageUrl, uploadId) => {
  //   const urls = getImageUrls(imageUrl, uploadId);
  //   return urls.length > 0 ? urls[0] : null;
  // }, [getImageUrls]);

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <h2>Loading work uploads...</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--cc-text-muted)';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 'clamp(20px, 2.2vw, 22px)', fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>{project?.name}</h1>
          {project?.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project?.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project?.postcode}
          </p>
        </div>
      </div>
      {/* Work Uploads Feed */}
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 16,
        padding: 32,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        minHeight: 300,
        maxWidth: 1100,
        margin: '0 auto',
        width: '90%'
      }}>
        {/* Header: title left, upload button right */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>
            Media & comments
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => setExportSelectionMode((v) => !v)}
              disabled={!project || project?.isFinished}
              aria-pressed={exportSelectionMode}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                padding: 0,
                background: exportSelectionMode
                  ? 'var(--branding-app-primary)'
                  : 'var(--branding-app-surface-panel)',
                color: exportSelectionMode
                  ? 'var(--branding-app-primary-fg)'
                  : 'var(--cc-text-secondary)',
                border: `1px solid ${exportSelectionMode ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)'}`,
                borderRadius: 8,
                cursor: !project || project?.isFinished ? 'not-allowed' : 'pointer',
                opacity: !project || project?.isFinished ? 0.5 : 1,
                boxSizing: 'border-box',
              }}
              title={
                project?.isFinished
                  ? 'Project finished'
                  : exportSelectionMode
                    ? 'Cancel selection'
                    : 'PDF report: select posts'
              }
            >
              <FileDown size={20} />
            </button>
            <button
              type="button"
              onClick={() => {
                if (isReadOnly) return;
                setShowUploadModal(true);
              }}
              disabled={isReadOnly}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isReadOnly ? '#9ca3af' : 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontWeight: 500,
                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                fontSize: 14,
                opacity: isReadOnly ? 0.6 : 1,
              }}
              title={project?.isFinished ? 'Project finished - no new posts' : (isReadOnly ? 'You do not have permission to post' : 'Upload work')}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        {exportSelectionMode && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
              padding: '12px 16px',
              background: 'var(--branding-app-surface-panel)',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--cc-text-primary)', fontSize: 15 }}>Selected posts</span>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                color: 'var(--cc-text-primary)',
                cursor: visibleExportIds.length === 0 ? 'not-allowed' : 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                ref={selectAllVisibleInputRef}
                type="checkbox"
                checked={allVisibleSelected}
                disabled={visibleExportIds.length === 0}
                onChange={toggleSelectAllVisibleForExport}
                style={{ width: 18, height: 18, cursor: visibleExportIds.length === 0 ? 'not-allowed' : 'pointer' }}
                aria-label={`Select all posts in the current list (${visibleExportIds.length})`}
              />
              All in view
              <span style={{ color: 'var(--cc-text-muted)', fontWeight: 500 }}>({visibleExportIds.length})</span>
            </label>
            <button
              type="button"
              onClick={clearExportSelection}
              style={{
                background: 'var(--cc-cancel-bg)',
                color: 'var(--cc-text-primary)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
            {isPM && exportSelectedIds.size > 0 && (
              <button
                type="button"
                onClick={() => { setEditingSection(null); setSectionModalOpen(true); }}
                style={{
                  background: 'var(--branding-app-primary)',
                  color: 'var(--branding-app-primary-fg)',
                  border: 'none', borderRadius: 6, padding: '6px 14px',
                  fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
                title="Save selected posts as a named report section"
              >
                Save as section
              </button>
            )}
            <button
              type="button"
              onClick={handleDownloadSelectedMedia}
              disabled={exportSelectedIds.size === 0 || isDownloading || isExporting}
              style={{
                background:
                  exportSelectedIds.size === 0 || isDownloading || isExporting
                    ? '#cbd5e1'
                    : 'var(--branding-app-primary)',
                color:
                  exportSelectedIds.size === 0 || isDownloading || isExporting
                    ? '#64748b'
                    : 'var(--branding-app-primary-fg)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontWeight: 600,
                fontSize: 14,
                cursor:
                  exportSelectedIds.size === 0 || isDownloading || isExporting ? 'not-allowed' : 'pointer',
              }}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </button>
            <button
              type="button"
              onClick={() => setPdfExportSettingsOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                padding: 0,
                background: 'var(--cc-card-bg)',
                color: 'var(--cc-text-secondary)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 8,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
              title="PDF export options"
              aria-label="PDF export options"
            >
              <Settings size={20} />
            </button>
            <button
              type="button"
              onClick={handleGenerateActivityPdf}
              disabled={
                (reportSections.length === 0 && (exportSelectedIds.size === 0 || !selectionIncludesPdfPhotos)) ||
                isDownloading ||
                isExporting ||
                isSectionExporting ||
                usersMapLoading
              }
              title={
                reportSections.length > 0
                  ? 'Export all sections as PDF report'
                  : exportSelectedIds.size === 0
                    ? undefined
                    : usersMapLoading
                      ? 'Loading member names…'
                      : !selectionIncludesPdfPhotos
                        ? 'No photos in the selected posts. PDF can only include still images. Use Download for videos.'
                        : 'Create PDF from selected posts (photos only)'
              }
              style={{
                background: 'var(--cc-card-bg)',
                color:
                  (reportSections.length === 0 && (exportSelectedIds.size === 0 || !selectionIncludesPdfPhotos)) ||
                  isDownloading ||
                  isExporting ||
                  isSectionExporting ||
                  usersMapLoading
                    ? 'var(--cc-text-muted)'
                    : 'var(--cc-text-primary)',
                border: '1px solid var(--branding-app-surface-panel-border)',
                borderRadius: 6,
                padding: '6px 14px',
                fontWeight: 600,
                fontSize: 14,
                cursor:
                  (reportSections.length === 0 && (exportSelectedIds.size === 0 || !selectionIncludesPdfPhotos)) ||
                  isDownloading ||
                  isExporting ||
                  isSectionExporting ||
                  usersMapLoading
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isExporting || isSectionExporting ? 'Generating…' : usersMapLoading ? 'Loading names…' : 'PDF report'}
            </button>
            <span style={{ color: 'var(--cc-text-muted)', fontSize: 14 }}>
              {exportSelectedIds.size} selected
            </span>
            <div
              style={{
                flexBasis: '100%',
                width: '100%',
                fontSize: 12,
                color: 'var(--cc-text-muted)',
                lineHeight: 1.45,
                marginTop: -4,
              }}
            >
              PDF reports include <strong style={{ color: 'var(--cc-text-secondary)', fontWeight: 600 }}>photos only</strong>.
              Videos cannot be embedded — they are greyed while selecting. Use{' '}
              <strong style={{ color: 'var(--cc-text-secondary)', fontWeight: 600 }}>Download</strong> for video files.
            </div>
          </div>
        )}

        {/* PM report sections panel */}
        {isPM && (
          <ReportSectionsPanel
            sections={reportSections}
            onEdit={(section) => { setEditingSection(section); setSectionModalOpen(true); }}
            onDelete={handleDeleteSection}
            onExportSections={handleExportSectionReport}
            isExporting={isSectionExporting}
          />
        )}

        {/* Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setCompactView(v => !v)}
              style={{
                background: compactView ? 'var(--branding-app-primary)' : 'var(--cc-cancel-bg)',
                color: compactView ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-primary)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 16px',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: 15
              }}
            >
              {compactView ? 'Card View' : 'List View'}
            </button>
            {!project?.isFinished && (
              <button
                type="button"
                onClick={() => setExportSelectionMode((v) => !v)}
                aria-pressed={exportSelectionMode}
                style={{
                  background: exportSelectionMode ? 'var(--branding-app-primary)' : 'var(--cc-cancel-bg)',
                  color: exportSelectionMode ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-primary)',
                  border: `1px solid ${exportSelectionMode ? 'var(--branding-app-primary)' : 'var(--branding-app-surface-panel-border)'}`,
                  borderRadius: 6,
                  padding: '6px 16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 15,
                }}
                title={exportSelectionMode ? 'Hide checkboxes' : 'Select posts to download photos & videos'}
              >
                {exportSelectionMode ? 'Done selecting' : 'Select posts'}
              </button>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              {[
                { id: 'all', label: 'All' },
                { id: 'today', label: 'Today' },
                { id: 'week', label: 'This week' },
                { id: 'month', label: 'This month' },
              ].map(({ id, label }) => {
                const active = datePreset === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => applyDatePreset(id)}
                    style={{
                      background: active ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                      color: active ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                      border: '1px solid var(--branding-app-surface-panel-border)',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: 14,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={openDateRangeModal}
                title="Custom date range"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 36,
                  padding: 0,
                  background: datePreset === 'custom' ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                  color: datePreset === 'custom' ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <Calendar size={18} />
              </button>
            </div>
          </div>
        </div>

        {showDateRangeModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-date-range-title"
            onClick={() => setShowDateRangeModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.4)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--cc-card-bg)',
                borderRadius: 12,
                padding: 24,
                maxWidth: 400,
                width: '100%',
                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
              }}
            >
              <h3
                id="activity-date-range-title"
                style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600, color: 'var(--cc-text-primary)' }}
              >
                Custom date range
              </h3>
              <DateRangePicker
                startDate={modalRangeStart}
                endDate={modalRangeEnd}
                onStartDateChange={setModalRangeStart}
                onEndDateChange={setModalRangeEnd}
                onClear={() => {
                  setModalRangeStart('');
                  setModalRangeEnd('');
                }}
                placeholder="Start date – End date"
                fullWidth
                dropdownZIndex={2000}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => setShowDateRangeModal(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    background: 'var(--cc-card-bg)',
                    color: 'var(--cc-text-secondary)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyCustomDateRange}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          overflowX: 'auto',
          padding: '0.5rem 0'
        }}>
          <button
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              background: !selectedTeam ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
              color: !selectedTeam ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
              cursor: 'pointer',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
            onClick={() => setSelectedTeam(null)}
          >
            All Teams
          </button>
          {uniqueTeams.map(team => (
            <button
              key={team.id}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                background: selectedTeam === team.id ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                color: selectedTeam === team.id ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                cursor: 'pointer',
                border: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onClick={() => setSelectedTeam(team.id)}
            >
              <span style={{
                display: 'inline-block',
                width: '0.75rem',
                height: '0.75rem',
                borderRadius: '50%',
                background: getTeamColor(team.id),
                border: '1px solid var(--branding-app-surface-panel-border)',
                flexShrink: 0
              }} />
              {team.name}
            </button>
          ))}
        </div>

        {filteredUploads.length === 0 ? (
          <div style={{ color: 'var(--cc-text-muted)', fontSize: 16 }}>
            No work uploads recorded yet
          </div>
        ) : uploadsMatchingDate.length === 0 ? (
          <div style={{ color: 'var(--cc-text-muted)', fontSize: 16 }}>
            No work uploads in this date range
          </div>
        ) : (
          compactView ? (
            <ul style={{ padding: 0, listStyle: 'none', margin: 0 }}>
              {uploadsMatchingDate.map((upload) => {
                  const user = upload.userId ? users[upload.userId] : undefined;
                  const authorLabel = getWorkUploadAuthorLabel(upload);
                  const nameLoading = Boolean(upload.userId && usersMapLoading && !user);
                  return (
                    <li
                      key={upload.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'var(--branding-app-surface-panel)',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 8,
                        border: '1px solid var(--branding-app-surface-panel-border)',
                        fontSize: 15,
                        gap: 10
                      }}
                    >
                      {exportSelectionMode && (
                        <input
                          type="checkbox"
                          checked={exportSelectedIds.has(upload.id)}
                          onChange={() => toggleExportSelect(upload.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Select for PDF export"
                          style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                        />
                      )}
                      {/* Team Color */}
                      {upload.teamName && (
                        <span style={{
                          display: 'inline-block',
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: getTeamColor(upload.teamId),
                          marginRight: 6,
                          border: '1px solid var(--branding-app-surface-panel-border)'
                        }} 
                        title={`Team: ${upload.teamName}, ID: ${upload.teamId}, Color: ${getTeamColor(upload.teamId)}`}
                        />
                      )}
                      {/* User Name */}
                      <span style={{ 
                        fontWeight: 500, 
                        color: user ? 'var(--cc-text-primary)' : nameLoading ? 'var(--cc-text-muted)' : 'var(--branding-app-surface-panel-hint)',
                        marginRight: 8,
                        fontStyle: user ? 'normal' : nameLoading ? 'normal' : 'italic'
                      }}
                      title={nameLoading ? 'Loading member name…' : undefined}
                      >
                        {authorLabel}
                      </span>
                      {/* Team Name */}
                      {upload.teamName && (
                        <span style={{ color: 'var(--cc-text-muted)', fontSize: 14, marginRight: 8 }}>{upload.teamName}</span>
                      )}
                      {/* Media thumbnails (photos + videos) */}
                      {(() => {
                        const mediaItems = getWorkUploadMediaItems(upload);
                        if (mediaItems.length === 0) return null;
                        return (
                          <div style={{ display: 'flex', gap: 4, marginRight: 8, flexShrink: 0 }}>
                            {mediaItems.slice(0, 3).map((item, idx) => {
                              const openModal = (e) => {
                                e.stopPropagation();
                                setMediaModal({ open: true, items: mediaItems, initialIndex: idx });
                              };
                              if (item.type === 'image') {
                                const imageKey = `${upload.id}-compact-img-${idx}`;
                                const isLoaded = loadedImages.has(imageKey);
                                return (
                                  <div key={`${upload.id}-c-${idx}`} className="activity-image-loading-container" style={{ position: 'relative', width: 60, height: 60 }}>
                                    {!isLoaded && (
                                      <div className="activity-image activity-image-compact activity-image-skeleton activity-image-skeleton-compact" />
                                    )}
                                    <img
                                      src={item.url}
                                      alt=""
                                      className={`activity-image activity-image-compact ${isLoaded ? 'loaded' : ''}`}
                                      loading="lazy"
                                      onClick={openModal}
                                      onLoad={() => setLoadedImages((prev) => new Set(prev).add(imageKey))}
                                    />
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={`${upload.id}-c-${idx}`}
                                  role="button"
                                  tabIndex={0}
                                  title={
                                    exportSelectionMode
                                      ? 'Video — not included in PDF (use Download)'
                                      : 'Video'
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openModal(e);
                                    }
                                  }}
                                  style={{
                                    position: 'relative',
                                    width: 60,
                                    height: 60,
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    border: '3px solid var(--branding-app-surface-panel-border)',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    background: '#0f172a',
                                    opacity: exportSelectionMode ? 0.52 : 1,
                                    filter: exportSelectionMode ? 'grayscale(40%)' : 'none',
                                  }}
                                  onClick={openModal}
                                >
                                  <video
                                    src={item.url}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      display: 'block',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      inset: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'rgba(0,0,0,0.35)',
                                      pointerEvents: 'none',
                                    }}
                                  >
                                    <span style={{ color: '#fff', fontSize: 14, paddingLeft: 2 }} aria-hidden>▶</span>
                                  </div>
                                  {exportSelectionMode && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: 2,
                                        bottom: 2,
                                        right: 2,
                                        pointerEvents: 'none',
                                        zIndex: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'block',
                                          fontSize: 8,
                                          fontWeight: 700,
                                          color: '#0f172a',
                                          background: 'rgba(255,255,255,0.92)',
                                          padding: '1px 4px',
                                          borderRadius: 3,
                                          lineHeight: 1.2,
                                          textAlign: 'center',
                                        }}
                                      >
                                        No PDF
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {mediaItems.length > 3 && (
                              <div
                                className="activity-image-counter"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMediaModal({ open: true, items: mediaItems, initialIndex: 3 });
                                }}
                              >
                                +{mediaItems.length - 3}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Comment (truncated) */}
                      {upload.comment && (
                        <span style={{
                          color: 'var(--cc-text-secondary)',
                          fontSize: 14,
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginRight: 8
                        }}
                          title={upload.comment}
                        >
                          {upload.comment}
                        </span>
                      )}
                      {/* Voice Note */}
                      {voiceNotes[upload.id] && (
                        <div style={{ marginRight: 8, flexShrink: 0 }}>
                          <VoicePlayer
                            voiceUrl={voiceNotes[upload.id]}
                            uploadId={upload.id}
                            userId={upload.userId}
                            compact
                          />
                        </div>
                      )}
                      {/* Comment count (compact) */}
                      <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontSize: 12 }}>
                        {(commentsByUpload[upload.id] || []).length} comment{((commentsByUpload[upload.id] || []).length) !== 1 ? 's' : ''}
                      </span>
                      {/* Timestamp + Delete */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        {currentUserId === upload.userId && (
                          <button
                            type="button"
                            onClick={() => {
                              setUploadToDelete(upload);
                              setShowDeleteConfirm(true);
                            }}
                            disabled={deletingId === upload.id}
                            style={{
                              background: 'transparent',
                              color: '#dc2626',
                              border: '1px solid #dc2626',
                              borderRadius: 6,
                              padding: '4px 10px',
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: deletingId === upload.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s ease',
                            }}
                            title="Delete post"
                            onMouseEnter={(e) => {
                              if (deletingId !== upload.id) {
                                e.currentTarget.style.background = '#dc2626';
                                e.currentTarget.style.color = '#fff';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = '#dc2626';
                            }}
                          >
                            {deletingId === upload.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                        <span style={{ color: 'var(--branding-app-surface-panel-hint)', fontSize: 13 }}>
                          {formatTimestamp(upload.timestamp)}
                        </span>
                      </div>
                    </li>
                  );
                })}
            </ul>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {uploadsMatchingDate.map((upload) => {
                  const user = upload.userId ? users[upload.userId] : undefined;
                  const authorLabel = getWorkUploadAuthorLabel(upload);
                  const nameLoading = Boolean(upload.userId && usersMapLoading && !user);
                  return (
                    <div
                      key={upload.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 20,
                        borderRadius: 12,
                        backgroundColor: 'var(--branding-app-surface-panel)',
                        border: '1px solid var(--branding-app-surface-panel-border)'
                      }}
                    >
                      {/* Header with User Info */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 16
                      }}>
                        {exportSelectionMode && (
                          <input
                            type="checkbox"
                            checked={exportSelectedIds.has(upload.id)}
                            onChange={() => toggleExportSelect(upload.id)}
                            aria-label="Select for PDF export"
                            style={{ width: 20, height: 20, cursor: 'pointer', flexShrink: 0, marginTop: 4 }}
                          />
                        )}
                        {/* User Profile Picture */}
                        {user?.profilePicture && (
                          <img
                            src={user.profilePicture}
                            alt=""
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 6,
                              objectFit: 'cover',
                              border: '1px solid var(--branding-app-surface-panel-border)'
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          {/* User Name */}
                          <div style={{
                            fontSize: 15,
                            fontWeight: 500,
                            color: user ? 'var(--cc-text-primary)' : nameLoading ? 'var(--cc-text-muted)' : 'var(--branding-app-surface-panel-hint)',
                            marginBottom: 4,
                            fontStyle: user ? 'normal' : nameLoading ? 'normal' : 'italic'
                          }}
                          title={nameLoading ? 'Loading member name…' : undefined}
                          >
                            {authorLabel}
                          </div>
                          {/* Team Name with Color */}
                          {upload.teamName && (
                            <div style={{
                              fontSize: 13,
                              color: 'var(--cc-text-muted)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}>
                              <span style={{
                                display: 'inline-block',
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: getTeamColor(upload.teamId),
                                marginRight: 8,
                                border: '1px solid var(--branding-app-surface-panel-border)'
                              }} 
                              title={`Team: ${upload.teamName}, ID: ${upload.teamId}, Color: ${getTeamColor(upload.teamId)}`}
                              />
                              {upload.teamName}
                            </div>
                          )}
                        </div>
                        {/* Timestamp + Delete */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {currentUserId === upload.userId && (
                            <button
                              type="button"
                              onClick={() => {
                                setUploadToDelete(upload);
                                setShowDeleteConfirm(true);
                              }}
                              disabled={deletingId === upload.id}
                              style={{
                                background: 'transparent',
                                color: '#dc2626',
                                border: '1px solid #dc2626',
                                borderRadius: 6,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: deletingId === upload.id ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                              }}
                              title="Delete post"
                              onMouseEnter={(e) => {
                                if (deletingId !== upload.id) {
                                  e.currentTarget.style.background = '#dc2626';
                                  e.currentTarget.style.color = '#fff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#dc2626';
                              }}
                            >
                              {deletingId === upload.id ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                          <span style={{ fontSize: 13, color: 'var(--branding-app-surface-panel-hint)' }}>
                            {formatTimestamp(upload.timestamp)}
                          </span>
                        </div>
                      </div>
                      {/* Media gallery: photos + videos (unified grid / lightbox) */}
                      {(() => {
                        const mediaItems = getWorkUploadMediaItems(upload);
                        if (mediaItems.length === 0) return null;
                        const nImg = mediaItems.filter((m) => m.type === 'image').length;
                        const nVid = mediaItems.filter((m) => m.type === 'video').length;
                        const openAt = (idx) => () =>
                          setMediaModal({ open: true, items: mediaItems, initialIndex: idx });
                        const dimVideoForPdf = exportSelectionMode;
                        const videoPoster = (url) => (
                          <>
                            <video
                              src={url}
                              muted
                              playsInline
                              preload="metadata"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                pointerEvents: 'none',
                                opacity: dimVideoForPdf ? 0.52 : 1,
                                filter: dimVideoForPdf ? 'grayscale(40%)' : 'none',
                              }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.4) 100%)',
                                pointerEvents: 'none',
                              }}
                            >
                              <div
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '50%',
                                  background: 'rgba(255,255,255,0.95)',
                                  color: 'var(--branding-app-primary, #2563eb)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 16,
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                                  paddingLeft: 3,
                                }}
                                aria-hidden
                              >
                                ▶
                              </div>
                            </div>
                            {dimVideoForPdf && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  zIndex: 4,
                                  pointerEvents: 'none',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    background: 'rgba(255,255,255,0.94)',
                                    padding: '4px 8px',
                                    borderRadius: 6,
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                  }}
                                >
                                  Not in PDF
                                </span>
                              </div>
                            )}
                          </>
                        );
                        return (
                          <div
                            style={{
                              marginBottom: 16,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 12,
                              width: '100%',
                              maxWidth: 720,
                              marginLeft: 'auto',
                              marginRight: 'auto',
                            }}
                          >
                            {mediaItems.length === 1 && mediaItems[0].type === 'image' ? (
                              <div className="activity-image-single-container" onClick={openAt(0)}>
                                {(() => {
                                  const imageKey = `${upload.id}-single`;
                                  const isLoaded = loadedImages.has(imageKey);
                                  return (
                                    <div style={{ position: 'relative', width: '100%' }}>
                                      {!isLoaded && (
                                        <div className="activity-image-skeleton activity-image-skeleton-single" />
                                      )}
                                      <img
                                        src={mediaItems[0].url}
                                        alt="Work upload"
                                        className={`activity-image-single ${isLoaded ? 'loaded' : ''}`}
                                        loading="lazy"
                                        onLoad={() => setLoadedImages((prev) => new Set(prev).add(imageKey))}
                                      />
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : mediaItems.length === 1 && mediaItems[0].type === 'video' ? (
                              <div
                                className="activity-image-single-container"
                                style={{
                                  maxWidth: 560,
                                  cursor: 'pointer',
                                  opacity: exportSelectionMode ? 0.92 : 1,
                                }}
                                onClick={openAt(0)}
                                title={
                                  exportSelectionMode
                                    ? 'Video — not included in PDF (use Download)'
                                    : undefined
                                }
                              >
                                <div
                                  style={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '16 / 9',
                                    background: '#0f172a',
                                  }}
                                >
                                  {videoPoster(mediaItems[0].url)}
                                </div>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                  gap: 12,
                                }}
                              >
                                {mediaItems.map((item, index) => {
                                  if (item.type === 'image') {
                                    const imageKey = `${upload.id}-grid-${index}`;
                                    const isLoaded = loadedImages.has(imageKey);
                                    return (
                                      <div
                                        key={`${upload.id}-m-${index}`}
                                        className="activity-image-grid-container"
                                        style={{ position: 'relative' }}
                                        onClick={openAt(index)}
                                      >
                                        {!isLoaded && (
                                          <div className="activity-image-skeleton activity-image-skeleton-grid" />
                                        )}
                                        <img
                                          src={item.url}
                                          alt={`Work upload ${index + 1}`}
                                          className={`activity-image-grid ${isLoaded ? 'loaded' : ''}`}
                                          loading="lazy"
                                          onLoad={() => setLoadedImages((prev) => new Set(prev).add(imageKey))}
                                        />
                                      </div>
                                    );
                                  }
                                  return (
                                    <div
                                      key={`${upload.id}-m-${index}`}
                                      className="activity-image-grid-container"
                                      style={{ position: 'relative', cursor: 'pointer' }}
                                      onClick={openAt(index)}
                                      title={
                                        exportSelectionMode
                                          ? 'Video — not included in PDF (use Download)'
                                          : 'Open video'
                                      }
                                    >
                                      {videoPoster(item.url)}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {mediaItems.length > 1 && (
                              <div
                                style={{
                                  textAlign: 'center',
                                  fontSize: 14,
                                  color: 'var(--cc-text-muted)',
                                  marginTop: 8,
                                }}
                              >
                                {[
                                  nImg > 0 ? `${nImg} photo${nImg !== 1 ? 's' : ''}` : null,
                                  nVid > 0 ? `${nVid} video${nVid !== 1 ? 's' : ''}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                                {exportSelectionMode && nVid > 0
                                  ? ' · PDF uses photos only'
                                  : ''}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {/* Comment */}
                      {upload.comment && (
                        <div style={{
                          fontSize: 15,
                          color: 'var(--cc-text-primary)',
                          lineHeight: 1.5,
                          padding: '12px 16px',
                          background: 'var(--cc-card-bg)',
                          borderRadius: 8,
                          border: '1px solid var(--branding-app-surface-panel-border)',
                          marginBottom: 16
                        }}>
                          {upload.comment}
                        </div>
                      )}
                      {/* Voice Note */}
                      {voiceNotes[upload.id] && (
                        <VoicePlayer 
                          voiceUrl={voiceNotes[upload.id]} 
                          uploadId={upload.id} 
                          userId={upload.userId}
                        />
                      )}
                      {/* Comments */}
                      <div style={{ marginTop: 16, borderTop: '1px solid var(--branding-app-surface-panel-border)', paddingTop: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                          <button
                            onClick={() => toggleComments(upload.id)}
                            style={{
                              background: 'none', border: 'none', color: 'var(--cc-text-muted)', fontSize: 14, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6
                            }}
                          >
                            {(commentsByUpload[upload.id] || []).length} comment{(commentsByUpload[upload.id] || []).length !== 1 ? 's' : ''}
                            <span style={{ fontSize: 12 }}>{expandedComments.has(upload.id) ? '▼' : '▶'}</span>
                          </button>
                          {!isReadOnly && (
                          <button
                            onClick={() => setCommentModal({ open: true, uploadId: upload.id, parentCommentId: null })}
                            style={{
                              background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13
                            }}
                          >Add comment</button>
                          )}
                        </div>
                        {expandedComments.has(upload.id) && (
                          <div style={{ marginTop: 12 }}>
                            {((commentsByUpload[upload.id] || []).filter(c => !c.parentCommentId)).map(comment => (
                              <div key={comment.id} style={{ marginBottom: 12 }}>
                                <div style={{
                                  padding: '10px 14px', background: 'var(--cc-card-bg)', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)',
                                  display: 'flex', flexDirection: 'column', gap: 6
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--cc-text-primary)' }}>{comment.createdByName}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)' }}>{comment.createdAt && formatTimestamp(comment.createdAt)}</span>
                                      {canDeleteComment(comment) && (
                                        <button
                                          onClick={() => setDeleteCommentModal({ open: true, uploadId: upload.id, commentId: comment.id, comment })}
                                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}
                                        >Delete</button>
                                      )}
                                    </div>
                                  </div>
                                  {comment.comment && <p style={{ margin: 0, fontSize: 14, color: 'var(--cc-text-primary)', lineHeight: 1.5 }}>{comment.comment}</p>}
                                  {comment.photoUrls?.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                                      {comment.photoUrls.map((url, i) => (
                                        <img
                                          key={i}
                                          src={url}
                                          alt=""
                                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                                          onClick={() =>
                                            setMediaModal({
                                              open: true,
                                              items: comment.photoUrls.map((u) => ({ type: 'image', url: u })),
                                              initialIndex: i,
                                            })
                                          }
                                        />
                                      ))}
                                    </div>
                                  )}
                                  {comment.voiceNoteUrl && (
                                    <VoicePlayer voiceUrl={comment.voiceNoteUrl} uploadId={upload.id} userId={comment.createdBy} />
                                  )}
                                  {!isReadOnly && (
                                  <button
                                    onClick={() => setCommentModal({ open: true, uploadId: upload.id, parentCommentId: comment.id })}
                                    style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--cc-text-muted)', fontSize: 12, cursor: 'pointer' }}
                                  >Reply</button>
                                  )}
                                </div>
                                {(commentsByUpload[upload.id] || []).filter(c => c.parentCommentId === comment.id).map(reply => (
                                  <div key={reply.id} style={{ marginLeft: 20, marginTop: 8, padding: '8px 12px', background: 'var(--branding-app-surface-panel)', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--cc-text-primary)' }}>{reply.createdByName}</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 11, color: 'var(--branding-app-surface-panel-hint)' }}>{reply.createdAt && formatTimestamp(reply.createdAt)}</span>
                                        {canDeleteComment(reply) && (
                                          <button onClick={() => setDeleteCommentModal({ open: true, uploadId: upload.id, commentId: reply.id, comment: reply })} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>Delete</button>
                                        )}
                                      </div>
                                    </div>
                                    {reply.comment && <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--cc-text-primary)' }}>{reply.comment}</p>}
                                    {reply.photoUrls?.length > 0 && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                                        {reply.photoUrls.map((url, i) => (
                                          <img key={i} src={url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                        ))}
                                      </div>
                                    )}
                                    {reply.voiceNoteUrl && <VoicePlayer voiceUrl={reply.voiceNoteUrl} uploadId={upload.id} userId={reply.createdBy} />}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )
        )}
      </div>
      <ActivityMediaModal
        open={mediaModal.open}
        onClose={() => setMediaModal({ open: false, items: [], initialIndex: 0 })}
        items={mediaModal.items}
        initialIndex={mediaModal.initialIndex}
      />

      {/* Delete Activity Post Confirmation Modal */}
      {showDeleteConfirm && uploadToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: 16,
            padding: 32,
            maxWidth: 400,
            width: '90vw',
            textAlign: 'center',
          }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fef2f2',
              border: '2px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
            }}>
              <AlertTriangle size={32} color="#dc2626" />
            </div>

            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--cc-text-primary)',
            }}>
              Delete Activity Post?
            </h3>

            <p style={{
              margin: '0 0 24px 0',
              fontSize: 14,
              color: 'var(--cc-text-muted)',
              lineHeight: 1.5,
            }}>
              Are you sure you want to delete this post? This will permanently remove the photos
              {getVideoUrls(uploadToDelete).length > 0 ? ', videos' : ''}
              {uploadToDelete.voiceNoteUrl ? ', voice note' : ''} and comment. This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setUploadToDelete(null);
                }}
                style={{
                  background: 'var(--cc-cancel-bg)',
                  color: 'var(--cc-text-primary)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 20px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteUpload}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 20px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                Delete Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Comment Modal */}
      <ActivityCommentModal
        open={commentModal.open}
        onClose={() => setCommentModal({ open: false, uploadId: null, parentCommentId: null })}
        uploadId={commentModal.uploadId}
        parentCommentId={commentModal.parentCommentId}
        onSubmit={handleAddComment}
      />

      {/* Delete Comment Confirmation */}
      {deleteCommentModal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{ background: 'var(--cc-card-bg)', padding: 24, borderRadius: 12, maxWidth: 400, width: '90%' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Delete comment?</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: 14, color: 'var(--cc-text-muted)' }}>This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteCommentModal({ open: false, uploadId: null, commentId: null, comment: null })} style={{ background: 'var(--cc-cancel-bg)', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmDeleteComment} style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Upload Modal */}
      <ActivityUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => setShowUploadModal(false)}
        projectId={projectId}
        teams={teams}
        isProjectFinished={project?.isFinished ?? false}
      />

      <PdfExportSettingsModal
        open={pdfExportSettingsOpen}
        onClose={() => setPdfExportSettingsOpen(false)}
        variant="activity"
        value={activityPdfSettings}
        onChange={setActivityPdfSettings}
        companyLogoAvailable={Boolean(companyLogoUrl)}
      />

      <ReportSectionModal
        open={sectionModalOpen}
        onClose={() => { setSectionModalOpen(false); setEditingSection(null); }}
        onSave={handleSaveSection}
        selectedCount={editingSection ? 0 : exportSelectedIds.size}
        existingSection={editingSection}
      />
    </div>
  );
}

export default withProjectPermissions(ActivityLanding, { requireEdit: true });